import React from 'react';
import {
  Button,
  Card,
  CardSection,
  Carousel,
  Gutter,
  Layout,
  LayoutItem,
  LargeHeader,
  useBreakpoint,
  DarkTheme,
} from '@axiom/ui';
import type { InstantMatch } from '@axiom/validation';

import { useUserAccountData } from '../../hooks/useUserAccountData';
import { useSubmissionsData } from '../../hooks/useSubmissionsData';

import { EngagementCarouselTileButton } from './EngagementCarouselTileButton';
import { EngagementCarouselEndCard } from './EngagementCarouselEndCard';
import { useEngagementCarouselSubmissionData } from './useEngagementCarouselSubmissionData';

export type EngagementCarouselProps = {
  maxDisplayCount?: number;
};

export const EngagementCarousel = ({
  maxDisplayCount = 8,
}: EngagementCarouselProps) => {
  const { isMobile } = useBreakpoint();
  const { account, user } = useUserAccountData();
  const { allSubmissions } = useSubmissionsData({
    account,
  });

  const submissions =
    allSubmissions.length > maxDisplayCount
      ? allSubmissions.slice(0, maxDisplayCount)
      : allSubmissions;
  const { allSubmissionsData, allInstantMatchCandidates } =
    useEngagementCarouselSubmissionData(submissions);

  return (
    <Card name="ENGAGEMENTS_CAROUSEL_CONTAINER">
      <CardSection background="blue">
        <Layout
          name="headerLayout"
          direction={isMobile ? 'vertical' : 'horizontal'}
          position={isMobile ? 'bottom' : 'middle'}
        >
          <LayoutItem fluid>
            <LargeHeader name="HEADER">
              Recent engagements ({submissions.length})
            </LargeHeader>
          </LayoutItem>
          <LayoutItem>
            <Button to="./engagements" name="VIEW_ENGAGEMENTS">
              View all engagements
            </Button>
          </LayoutItem>
        </Layout>
        <Gutter bottom="24px" />
        <DarkTheme backgroundName="none">
          {submissions.length > 0 ? (
            <Carousel
              name="ENGAGEMENTS_CAROUSEL"
              key={`engagements_carousel_${account.id}`}
            >
              {allSubmissionsData.map((submissionData, index) => {
                const { submission, submissionDetails } = submissionData;
                return (
                  <EngagementCarouselTileButton
                    name={`ENGAGEMENT_TILE_${index}`}
                    instantMatchCandidates={
                      allInstantMatchCandidates[index] as Array<
                        InstantMatch['candidate']
                      >
                    }
                    account={account}
                    submission={submission}
                    submissionDetails={submissionDetails}
                    key={`${submission.id}-${submission.submissionId}`}
                  />
                );
              })}
              <EngagementCarouselEndCard
                name="ENGAGEMENT_CAROUSEL_END_CARD"
                user={user}
                account={account}
                key="endCard"
              />
            </Carousel>
          ) : (
            <EngagementCarouselEndCard
              name="ENGAGEMENT_CAROUSEL_END_CARD"
              account={account}
              user={user}
            />
          )}
        </DarkTheme>
      </CardSection>
    </Card>
  );
};
